'use client'

import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { EventForm } from '@/features/calendar/event-form'
import { getMeetupById } from '@/services/meetups'

function CreateEventPage() {
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [meetupTitle, setMeetupTitle] = useState<string | null>(null)
  const navigate = useNavigate()
  const location = useLocation()

  // Extract meetupId from URL query parameters
  const queryParams = new URLSearchParams(location.search)
  const meetupId = queryParams.get('meetupId')

  // Fetch meetup details if meetupId is provided
  useEffect(() => {
    async function fetchMeetupDetails() {
      if (!meetupId) {
        setIsLoading(false)
        return
      }

      try {
        const { data, error } = await getMeetupById(meetupId)

        if (error) throw new Error(error)
        if (data) setMeetupTitle(data.title)
      } catch (err) {
        console.error('Error fetching meetup details:', err)
        setError(err instanceof Error ? err.message : 'Failed to load meetup details')
      } finally {
        setIsLoading(false)
      }
    }

    fetchMeetupDetails()
  }, [meetupId])

  // Handle successful event creation
  const handleEventCreated = (eventId: string) => {
    // Navigate back to the meetup page or to the event details
    if (meetupId) {
      navigate(`/meetups/${meetupId}`)
    } else {
      navigate(`/calendar`)
    }
  }

  // Handle cancel
  const handleCancel = () => {
    if (meetupId) {
      navigate(`/meetups/${meetupId}`)
    } else {
      navigate('/calendar')
    }
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : (
          <>
            {meetupId && meetupTitle && (
              <div className="mb-6 bg-primary/10 p-4 rounded-lg border border-primary/20">
                <h1 className="text-2xl font-bold text-white">Create Event for {meetupTitle}</h1>
                <p className="text-gray-400">
                  This event will be automatically associated with the {meetupTitle} meetup
                </p>
              </div>
            )}

            <EventForm
              defaultMeetupId={meetupId || undefined}
              onSuccess={handleEventCreated}
              onCancel={handleCancel}
            />
          </>
        )}
      </div>
    </div>
  )
}

export default CreateEventPage
